/**
 * BudgetChart Component
 * Visual chart showing budget progression (waterfall chart)
 */

import React from 'react';

export interface BudgetChartProps {
  data: {
    original: number;
    changes: number;
    committed: number;
    actual: number;
    forecast: number;
  };
  chartType?: 'waterfall' | 'bar';
}

const BudgetChart: React.FC<BudgetChartProps> = ({ data, chartType: _chartType = 'bar' }) => {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const revised = data.original + data.changes;
  const maxValue = Math.max(data.original, revised, data.committed, data.actual, data.forecast);

  const getPercentage = (value: number): number => {
    return maxValue > 0 ? (value / maxValue) * 100 : 0;
  };

  const chartData = [
    {
      label: 'Original Budget',
      value: data.original,
      color: 'bg-gray-600',
      percentage: getPercentage(data.original),
    },
    {
      label: 'Budget Changes',
      value: data.changes,
      color: data.changes >= 0 ? 'bg-green-600' : 'bg-red-600',
      percentage: getPercentage(Math.abs(data.changes)),
      isChange: true,
    },
    {
      label: 'Revised Budget',
      value: revised,
      color: 'bg-brand-600',
      percentage: getPercentage(revised),
    },
    {
      label: 'Committed',
      value: data.committed,
      color: 'bg-blue-600',
      percentage: getPercentage(data.committed),
    },
    {
      label: 'Actual Spent',
      value: data.actual,
      color: 'bg-purple-600',
      percentage: getPercentage(data.actual),
    },
    {
      label: 'Forecast',
      value: data.forecast,
      color: 'bg-orange-600',
      percentage: getPercentage(data.forecast),
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Budget Overview</h3>

      {/* Bar Chart */}
      <div className="space-y-4">
        {chartData.map((item, index) => (
          <div key={index}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{item.label}</span>
              <span className="text-sm font-semibold text-gray-900">
                {item.isChange && item.value > 0 && '+'}
                {formatCurrency(item.value)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-3 ${item.color} rounded-full transition-all duration-500`}
                style={{ width: `${item.percentage}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {chartData.map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-sm ${item.color}`} />
              <span className="text-xs text-gray-600">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-2 gap-4">
        <div>
          <span className="text-xs text-gray-600">Remaining Budget</span>
          <div
            className={`text-lg font-bold mt-1 ${
              revised - data.actual >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {formatCurrency(revised - data.actual)}
          </div>
        </div>
        <div>
          <span className="text-xs text-gray-600">Utilization</span>
          <div className="text-lg font-bold text-gray-900 mt-1">
            {revised > 0 ? ((data.actual / revised) * 100).toFixed(1) : 0}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetChart;
